import { useEffect, useState } from "react";
import { Building2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext.js";
import { fetchGovernanceTenants } from "../../lib/governance-api";
import { getAdminApiKey } from "../../lib/admin-settings";
import { getAdminJwt } from "../../lib/auth-storage.js";
import {
  getTenantExternalId,
  setTenantExternalId,
} from "../../lib/tenant-settings";

/**
 * Compact tenant indicator/switcher, folded into the TopBar (replaces the
 * standalone `TenantSwitcherBar` row to reclaim vertical space).
 *
 * - For tenant_admin / tenant_operator: read-only chip (their JWT is scoped).
 * - For platform credentials: dropdown to switch active tenant.
 *
 * Event-name constants are inlined to stay resilient to partial test mocks
 * (e.g. `vi.mock("../lib/auth-storage.js", () => ({ getAdminJwt: ... }))`
 * that omit the matching `*_CHANGED` constant exports).
 */
const TENANT_EXTERNAL_ID_CHANGED_EVENT =
  "identity-armour:tenant-external-id-changed";
const ADMIN_API_KEY_CHANGED_EVENT = "identity-armour:admin-api-key-changed";
const ADMIN_JWT_CHANGED_EVENT = "identity-armour:admin-jwt-changed";

export function TenantChip() {
  const { user } = useAuth();
  const [options, setOptions] = useState<{ externalId: string; name: string }[]>([]);
  const [current, setCurrent] = useState(() => {
    try {
      return getTenantExternalId();
    } catch {
      return "";
    }
  });

  useEffect(() => {
    const load = () => {
      try {
        setCurrent(getTenantExternalId());
      } catch {
        setCurrent("");
      }
      if (user?.role === "tenant_admin" || user?.role === "tenant_operator") {
        setOptions([]);
        return;
      }
      const key = (() => {
        try {
          return getAdminApiKey();
        } catch {
          return "";
        }
      })();
      const jwt = (() => {
        try {
          return getAdminJwt();
        } catch {
          return "";
        }
      })();
      if (!key && !jwt) {
        setOptions([]);
        return;
      }
      fetchGovernanceTenants()
        .then((res) => {
          setOptions(res.items.map((t) => ({ externalId: t.externalId, name: t.name })));
        })
        .catch(() => setOptions([]));
    };

    load();
    window.addEventListener(TENANT_EXTERNAL_ID_CHANGED_EVENT, load);
    window.addEventListener(ADMIN_API_KEY_CHANGED_EVENT, load);
    window.addEventListener(ADMIN_JWT_CHANGED_EVENT, load);
    return () => {
      window.removeEventListener(TENANT_EXTERNAL_ID_CHANGED_EVENT, load);
      window.removeEventListener(ADMIN_API_KEY_CHANGED_EVENT, load);
      window.removeEventListener(ADMIN_JWT_CHANGED_EVENT, load);
    };
  }, [user?.role, user?.id]);

  if (user?.role === "tenant_admin" || user?.role === "tenant_operator") {
    return (
      <div
        className="hidden md:flex items-center gap-1.5 rounded-md border border-card bg-card px-2 py-1 text-xs"
        title={current || "(no tenant)"}
      >
        <Building2 className="h-3.5 w-3.5 text-subtle" aria-hidden />
        <span className="font-mono text-muted truncate max-w-[160px]">
          {current || "(none)"}
        </span>
      </div>
    );
  }

  if (options.length === 0) {
    return current ? (
      <div
        className="hidden md:flex items-center gap-1.5 rounded-md border border-card bg-card px-2 py-1 text-xs"
        title={current}
      >
        <Building2 className="h-3.5 w-3.5 text-subtle" aria-hidden />
        <span className="font-mono text-muted truncate max-w-[160px]">{current}</span>
      </div>
    ) : null;
  }

  return (
    <div className="hidden md:flex items-center gap-1.5">
      <Building2 className="h-3.5 w-3.5 text-subtle" aria-hidden />
      <select
        className="input-field py-1 text-xs font-mono max-w-[220px]"
        value={current}
        onChange={(e) => {
          try {
            setTenantExternalId(e.target.value);
          } catch {
            /* ignore in test environments where this is a partial mock */
          }
          setCurrent(e.target.value);
        }}
        aria-label="Active tenant"
      >
        {options.map((o) => (
          <option key={o.externalId} value={o.externalId}>
            {o.name} ({o.externalId.slice(0, 10)}…)
          </option>
        ))}
      </select>
    </div>
  );
}
